
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

interface Client {
  id: string;
  user_id?: string;
  company_id?: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  contact_name?: string;
  contact_position?: string;
  status: string;
  logo_url?: string;
  logo_initials?: string;
  next_delivery?: string;
  visible_to?: string[];
  created_at: string;
  updated_at: string;
}

export function useRealtimeClients() {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadClients = async () => {
    if (!user) return;
    
    try {
      console.log('Loading clients for user:', user.id);
      setIsLoading(true);

      // First get the user's company_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (userError) {
        console.error('Error fetching user data:', userError);
        setClients([]);
        setIsLoading(false);
        return;
      }

      let targetCompanyId = userData?.company_id;

      // If user doesn't have company_id, check if they're a collaborator
      if (!targetCompanyId) {
        const { data: collaboratorData } = await supabase
          .from('collaborators')
          .select('company_id, permissions')
          .eq('collaborator_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (collaboratorData) {
          targetCompanyId = collaboratorData.company_id;
          console.log('User is collaborator, using company:', targetCompanyId);
        }
      }

      if (!targetCompanyId) {
        console.log('No company_id found for user');
        setClients([]);
        setIsLoading(false);
        return;
      }

      console.log('Loading clients for company:', targetCompanyId);

      // Load clients only for the user's company
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading clients:', error);
        setClients([]);
        setIsLoading(false);
        return;
      }

      console.log('Loaded clients:', data);
      
      const transformedClients: Client[] = (data || []).map(client => ({
        ...client,
        visible_to: Array.isArray(client.visible_to) ? client.visible_to.map(id => String(id)) : []
      }));

      setClients(transformedClients);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadClients();
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    console.log('Setting up realtime subscription for clients');
    
    const channel = supabase
      .channel('clients-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'clients'
        },
        (payload) => {
          console.log('Clients realtime update:', payload);
          loadClients();
        }
      )
      .subscribe();

    const collaboratorsChannel = supabase
      .channel('collaborators-clients-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'collaborators'
        },
        (payload) => {
          console.log('Collaborators change detected for clients:', payload);
          const newRecord = payload.new as Record<string, any> | null;
          const oldRecord = payload.old as Record<string, any> | null;
          
          if ((newRecord?.collaborator_id === user.id || oldRecord?.collaborator_id === user.id)) {
            console.log('Refreshing clients due to collaboration change');
            loadClients();
          }
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up clients realtime subscription');
      supabase.removeChannel(channel);
      supabase.removeChannel(collaboratorsChannel);
    };
  }, [user]);

  return {
    clients,
    isLoading,
    refreshClients: loadClients
  };
}
